import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { invalidateIfNotInFlight } from '@/patients/lib/invalidate-if-not-in-flight';
import type {
  MacroCollaborator,
  MacroData,
} from '@/types/settings-api';

/**
 * Trimmed save body for `POST /api/Doctor/CreateUpdateMacro`. The
 * wire shape on save is a SUBSET of the GET response — only the
 * client-writable fields. Server-managed fields are omitted entirely
 * (NOT shipped as null — the server stamps them on the response):
 *
 *   - `ownerName` / `ownerPhoto` — resolved from `ownerId`
 *   - `createdDate` / `createdBy` — set on create only
 *   - `updatedDate` / `updatedBy` — touched on every save
 *   - `macroHistories` — server appends a new snapshot on every save
 *
 * Verified against captured Add + Edit cURLs 2026-05-16.
 *
 * **Single endpoint for Add + Update** — backend branches on `id`:
 *   - `id: null` → INSERT (allocates a fresh GUID server-side)
 *   - `id: GUID` → UPDATE (matches existing macro, appends history row)
 *
 * Same pattern as `useSaveProviderBillingSettings` (PROD-763 PR-B).
 *
 * **`macroValue` must already be a stringified Delta JSON** at this
 * point — caller responsibility. See `MacroData.macroValue` JSDoc for
 * the parse / stringify contract.
 *
 * **`isDeleted: true` triggers the archive path** — the macro is
 * soft-deleted server-side. No separate DELETE endpoint exists; the
 * archive flow goes through this same save mutation with the flag set.
 */
export interface SaveMacroBody {
  /** Null on create, GUID on update. */
  id: string | null;
  organizationId: string;
  ownerId: string;
  macroKey: string;
  /** STRINGIFIED Quill Delta JSON. JSON.stringify(quillEditor.getContents().ops). */
  macroValue: string;
  /** Plaintext rendering for the list-preview surface (computed
   *  client-side from Quill's plain text). */
  macroValueFormatted: string;
  isGlobal: boolean;
  /** `true` triggers server-side archive (no separate DELETE endpoint). */
  isDeleted: boolean;
  /** Full collaborator list — server replaces the current set on save.
   *  Pass `[]` to clear. */
  macroCollaborators: MacroCollaborator[];
}

/**
 * Save (create OR update OR archive) a macro — PROD-764 PR-B.
 *
 * POST /api/Doctor/CreateUpdateMacro
 *
 * Flutter source: `settings_data_source_impl.dart:1438-1450`
 * (`createUpdateMacros`).
 *
 * The response is a string (per Flutter `Future<String>`) — likely an
 * id or status code, not the full macro. We invalidate the
 * provider-scoped macros cache on success so the list refetches and
 * picks up the server-allocated id + new history snapshot. Different
 * from the Billing surface (PROD-763 PR-B) where the POST response
 * was the full envelope — there setQueryData was safe; here we
 * `invalidateQueries` instead since we don't have the canonical
 * shape back on the wire.
 */
export function useSaveMacro(providerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SaveMacroBody) =>
      post<string>('/api/Doctor/CreateUpdateMacro', body),
    onSuccess: () => {
      // Invalidate every cached macros slot for this practitioner —
      // the search-term axis means there are multiple cache entries
      // (one per search query). Use a prefix predicate to clear them all.
      //
      // Dedup-aware via `invalidateIfNotInFlight` — the
      // `use-macro-realtime` hook ALSO invalidates the same predicate
      // on `MacroUpdated` SignalR events, including the echo of THIS
      // user's own save. Whichever invalidation arrives second sees
      // the first's fetch in flight and skips. Same pattern as the
      // chart-tab write surfaces.
      invalidateIfNotInFlight(queryClient, {
        predicate: (q) => {
          const key = q.queryKey;
          return (
            Array.isArray(key) &&
            key[0] === 'settings' &&
            key[1] === 'macros-by-provider-id' &&
            key[2] === providerId
          );
        },
      });
    },
  });
}

/**
 * Build a trimmed save body from a full `MacroData` plus the next
 * macroKey/macroValue/macroValueFormatted/isGlobal values. Drops
 * server-managed fields. Helper used by the editor dialog to
 * construct the POST body from the in-flight draft state.
 *
 * **`isDeleted` is ALWAYS present on the wire** (per @samer PR #180
 * review item 1). When the caller omits it (non-archive save), the
 * `?? false` coalesces to a literal `false` — the field is NOT
 * dropped from the JSON. .NET model binders default `bool` to `false`
 * regardless, but shipping it explicitly removes any ambiguity for
 * the next reader and matches the captured Add cURL exactly.
 *
 * **`macroValue` ships as the bare stringified ops array** (per
 * @samer PR #180 review item 2) — NOT a `{"ops":[...]}` envelope.
 * Verified against the GET response shape: `macroValue:
 * '[{"insert":"Hello world\\n"}]'`. Flutter's `flutter_quill` reads
 * the same bare-ops form, so the wire format round-trips between
 * platforms unchanged.
 */
export function buildSaveBody(
  source: MacroData,
  patch: {
    macroKey: string;
    macroValue: string;
    macroValueFormatted: string;
    isGlobal: boolean;
    isDeleted?: boolean;
  },
): SaveMacroBody {
  return {
    id: source.id || null,
    organizationId: source.organizationId,
    ownerId: source.ownerId,
    macroKey: patch.macroKey,
    macroValue: patch.macroValue,
    macroValueFormatted: patch.macroValueFormatted,
    isGlobal: patch.isGlobal,
    isDeleted: patch.isDeleted ?? false,
    macroCollaborators: source.macroCollaborators ?? [],
  };
}

/**
 * Override `macroId` on each selected collaborator before save —
 * mirrors Flutter `macro_tab.dart:998`:
 *
 *   selectedCollaborators.map((e) => e.copyWith(macroId: macroId))
 *
 * The single `MacroCollaborator` model is reused across two contexts
 * on the wire:
 *
 *   - **As a row on a macro** (from `getMacrosByProviderId`): `id` is
 *     the `macro_collaborators` table row-id, `macroId` populated.
 *   - **As a candidate** (from `GetProviderAndNurseList`): `id` is the
 *     user-side identifier, `macroId` empty.
 *
 * When the user saves the macro, every selected row needs `macroId`
 * stamped to the current macro's id so the backend can attach the
 * collaborator correctly. Without this stamp, a newly-picked candidate
 * would silently ship with `macroId: ''` and the association would
 * fail server-side — that was the bug fixed in commit `7ddcc8d`.
 *
 * **Edit-mode invariant**: when `isEdit === true`, `macroId` MUST be
 * non-empty (every existing macro has a server-allocated GUID). An
 * empty `macroId` on an Edit save with non-empty collaborators
 * indicates a different bug upstream — log to console so it's caught
 * in dev / Sentry; the function still returns the mapped list (no
 * crash in production) but ships the same broken state the fix was
 * meant to correct, surfaced loudly.
 *
 * **Add-mode**: `macroId` is `''` here on purpose — the server
 * allocates the macro id on insert and applies it to all collaborator
 * rows in the same transaction. The empty string is the wire's
 * "allocate me" signal.
 */
export function mapCollaboratorsForSave(
  macroId: string,
  collaborators: MacroCollaborator[],
  isEdit: boolean,
): MacroCollaborator[] {
  if (isEdit && macroId === '' && collaborators.length > 0) {
    // Invariant violation — never expected in Edit mode. Production
    // doesn't crash but the issue surfaces in dev console + any
    // attached error-reporting transport.
    // eslint-disable-next-line no-console
    console.error(
      '[mapCollaboratorsForSave] Edit-mode save with empty macroId — ' +
        'collaborators will ship without proper macroId association. ' +
        'Investigate the upstream source.id resolution.',
    );
  }
  return collaborators.map((c) => ({ ...c, macroId }));
}
